/*
 * Copyright 2018 Teralytics
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { DeckGL } from '@deck.gl/react';
import FlowMapLayer, { PickingType } from '@flowmap.gl/core';
import * as React from 'react';
import { StaticMap } from 'react-map-gl';
const FLOW_MAP_LAYER_ID = 'flow-map-layer';
const ESC_KEY = 'Escape';
export default class FlowMap extends React.Component {
    constructor() {
        super(...arguments);
        this.state = {
            viewState: this.props.initialViewState,
            time: 0,
        };
        this.animationFrame = -1;
        this.animate = () => {
            const loopLength = 1800; // unit corresponds to the timestamp in source data
            const animationSpeed = 30; // unit time per second
            const timestamp = Date.now() / 1000;
            const loopTime = loopLength / animationSpeed;
            this.setState({
                time: ((timestamp % loopTime) / loopTime) * loopLength,
            });
            this.animationFrame = window.requestAnimationFrame(this.animate);
        };
        this.handleFlowMapHover = (info) => {
            const { type, object } = info;
            switch (type) {
                case PickingType.FLOW: {
                    if (!object) {
                        this.highlight(undefined);
                    }
                    else {
                        this.highlight({
                            type: "flow" /* FLOW */,
                            flow: object,
                        }, info);
                    }
                    break;
                }
                case PickingType.LOCATION_AREA:
                case PickingType.LOCATION: {
                    const { getLocationId } = this.props;
                    if (!object) {
                        this.highlight(undefined);
                    }
                    else {
                        this.highlight({
                            type: type === PickingType.LOCATION_AREA ? "locationArea" /* LOCATION_AREA */ : "location" /* LOCATION */,
                            locationId: (getLocationId || FlowMapLayer.defaultProps.getLocationId.value)(object),
                        }, info);
                    }
                    break;
                }
            }
        };
        this.handleFlowMapClick = ({ type, object }) => {
            switch (type) {
                case PickingType.LOCATION_AREA:
                case PickingType.LOCATION: {
                    if (object) {
                        const { getLocationId, multiselect } = this.props;
                        const { selectedLocationIds } = this.state;
                        const locationId = (getLocationId || FlowMapLayer.defaultProps.getLocationId.value)(object);
                        const isSelected = selectedLocationIds && selectedLocationIds.indexOf(locationId) >= 0;
                        let next;
                        if (multiselect) {
                            if (selectedLocationIds) {
                                if (isSelected) {
                                    next = selectedLocationIds.filter(id => id !== locationId);
                                }
                                else {
                                    next = [...selectedLocationIds, locationId];
                                }
                            }
                            else {
                                next = [locationId];
                            }
                        }
                        else {
                            if (isSelected) {
                                next = undefined;
                            }
                            else {
                                next = [locationId];
                            }
                        }
                        this.selectLocations(next);
                        this.highlight(undefined);
                    }
                    break;
                }
            }
        };
        this.handleViewStateChange = ({ viewState }) => {
            this.setState({
                viewState,
                highlight: undefined,
            });
            const { onViewStateChange } = this.props;
            if (onViewStateChange) {
                onViewStateChange(viewState);
            }
        };
        this.handleKeyDown = (evt) => {
            if (evt instanceof KeyboardEvent && evt.key === ESC_KEY) {
                this.setState({
                    selectedLocationIds: undefined,
                    highlight: undefined,
                });
            }
        };
    }
    static getDerivedStateFromProps(props, state) {
        if (props.selectedLocationIds !== state.selectedLocationIds) {
            return {
                selectedLocationIds: props.selectedLocationIds,
            };
        }
        return null;
    }
    componentDidMount() {
        document.addEventListener('keydown', this.handleKeyDown);
        const { animate } = this.props;
        if (animate) {
            this.animate();
        }
        const { onViewStateChange } = this.props;
        if (onViewStateChange) {
            const { viewState } = this.state;
            if (viewState) {
                onViewStateChange(viewState);
            }
        }
    }
    componentDidUpdate(prevProps, prevState, snapshot) {
        const { animate } = this.props;
        if (animate !== prevProps.animate) {
            if (animate) {
                this.animate();
            }
            else {
                this.stopAnimation();
            }
        }
    }
    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyDown);
        this.stopAnimation();
    }
    render() {
        const { mapboxAccessToken, mapStyle, mixBlendMode } = this.props;
        const flowMapLayer = this.getFlowMapLayer();
        return (React.createElement(React.Fragment, null,
            React.createElement(DeckGL, { style: { mixBlendMode }, layers: [flowMapLayer], viewState: this.state.viewState, controller: true, onViewStateChange: this.handleViewStateChange },
                React.createElement(StaticMap, { width: "100%", height: "100%", mapboxApiAccessToken: mapboxAccessToken, mapStyle: mapStyle }))));
    }
    stopAnimation() {
        if (this.animationFrame) {
            window.cancelAnimationFrame(this.animationFrame);
        }
    }
    getFlowMapLayer() {
        const _a = this.props, { initialViewState, mapboxAccessToken, mixBlendMode, multiselect, animationTailLength, onSelected, onHighlighted } = _a, flowMapLayerProps = __rest(_a, ["initialViewState", "mapboxAccessToken", "mixBlendMode", "multiselect", "animationTailLength", "onSelected", "onHighlighted"]);
        const { highlight, selectedLocationIds } = this.state;
        return new FlowMapLayer(Object.assign(Object.assign({ id: FLOW_MAP_LAYER_ID, animationCurrentTime: this.state.time }, flowMapLayerProps), { selectedLocationIds,
            animationTailLength, highlightedLocationId: highlight && highlight.type === "location" /* LOCATION */ ? highlight.locationId : undefined, highlightedLocationAreaId: highlight && highlight.type === "locationArea" /* LOCATION_AREA */ ? highlight.locationId : undefined, highlightedFlow: highlight && highlight.type === "flow" /* FLOW */ ? highlight.flow : undefined, onHover: this.handleFlowMapHover, onClick: this.handleFlowMapClick }));
    }
    highlight(highlight, info) {
        this.setState({ highlight });
        const { onHighlighted } = this.props;
        if (onHighlighted) {
            onHighlighted(highlight, info);
        }
    }
    selectLocations(selectedLocationIds) {
        this.setState(state => (Object.assign(Object.assign({}, state), { selectedLocationIds })));
        const { onSelected } = this.props;
        if (onSelected) {
            onSelected(selectedLocationIds);
        }
    }
}
FlowMap.defaultProps = {
    mixBlendMode: 'multiply',
};
//# sourceMappingURL=FlowMap.js.map